import React, { useMemo, useState } from "react";

import moment from "moment";

import { useAdminUserContext } from "utils";

import { VersionReview } from "models";

import { AdminHead, AdminTable, Divider, Flex, Modal, PageSpin, Typography } from "components";

type VersionReviewsTableProps = {
  reviews: VersionReview[] | undefined;
  isLoading: boolean;
  description: string;
  groupLabel: (review: { baselineVersion: string; candidateVersion: string }) => string;
  detailLabel: (review: { baselineVersion: string; candidateVersion: string }) => React.ReactNode;
  openTooltip: string;
  onOpen: (record: { baselineVersion: string; candidateVersion: string }) => void;
};

export const VersionReviewsTable = ({ reviews, isLoading, description, groupLabel, detailLabel, openTooltip, onOpen }: VersionReviewsTableProps) => {
  const { user } = useAdminUserContext();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selectedReview = useMemo(() => reviews?.find((r) => r.revisions?.[0]?.id === selectedId) ?? null, [reviews, selectedId]);
  const selectedCurrent = selectedReview?.revisions?.[0] ?? null;

  // One section per group (as defined by groupLabel), each listing every reviewer's review for that group.
  const groups = useMemo(() => {
    const byGroup = new Map<string, { baselineVersion: string; candidateVersion: string; rows: Record<string, unknown>[] }>();
    (reviews ?? []).forEach((review) => {
      const current = review.revisions?.[0];
      const key = groupLabel(review);
      const row = {
        id: current?.id ?? "",
        reviewerId: review.reviewerId,
        baselineVersion: review.baselineVersion,
        candidateVersion: review.candidateVersion,
        score: current?.score ?? null,
        reviewer: review.reviewerName ? `${review.reviewerName} (${review.reviewerEmail ?? ""})` : review.reviewerEmail ?? "deleted admin",
        updatedAt: current ? moment(current.at).format("DD.MM.YYYY HH:mm") : "",
        revisions: review.revisions?.length ?? 0,
      };
      const group = byGroup.get(key);
      if (group) group.rows.push(row);
      else byGroup.set(key, { baselineVersion: review.baselineVersion, candidateVersion: review.candidateVersion, rows: [row] });
    });
    return Array.from(byGroup.entries()).map(([key, group]) => ({ key, ...group }));
  }, [reviews, groupLabel]);

  if (isLoading) return <PageSpin fullHeight={false} />;

  return (
    <>
      <AdminHead headline={`${reviews?.length ?? 0} version reviews`} description={description} />
      {groups.map((group) => (
        <Flex vertical gap="0.5rem" className="padding-top-200" key={group.key}>
          <Typography variant="h5">
            {group.key} ({group.rows.length})
          </Typography>
          {/* @ts-expect-error convert to TS */}
          <AdminTable
            dataSource={group.rows}
            config={{
              exclude: ["id", "reviewerId", "baselineVersion", "candidateVersion"],
              actions: [
                {
                  icon: "tabler/eye",
                  func: (record: { id: string }) => setSelectedId(record.id),
                  tooltip: "View comment and history",
                },
                {
                  icon: "tabler/arrow-right",
                  func: onOpen,
                  tooltip: openTooltip,
                  // Opening builds on top of the existing review, so it only makes sense for the admin who wrote it.
                  disabled: (record: { reviewerId: string }) => record.reviewerId !== user?.id,
                  disabledTooltip: "Only the reviewer who left this review can open it",
                },
              ],
            }}
          />
        </Flex>
      ))}

      <Modal
        title="Review detail"
        showModal={!!selectedId}
        onCancel={() => setSelectedId(null)}
        footer={null}
        maskClosable={true}
        content={
          selectedReview && selectedCurrent ? (
            <Flex vertical>
              <Typography variant="paragraph-m">{detailLabel(selectedReview)}</Typography>
              <Typography variant="paragraph-m" className="padding-top-200">
                Latest status
              </Typography>
              <Typography variant="paragraph-m">Score: {selectedCurrent.score}/10</Typography>
              <Typography variant="hero">Comment: {selectedCurrent.comment || "No comment left."}</Typography>
              <Divider />
              <Typography variant="paragraph-m" className="padding-top-200">
                Edit history
              </Typography>
              <ul>
                {selectedReview.revisions?.map((rev) => (
                  <li key={rev.id}>
                    {moment(rev.at).format("DD.MM.YYYY HH:mm")} — score {rev.score}/10 — {rev.comment || "no comment"}
                  </li>
                ))}
              </ul>
            </Flex>
          ) : null
        }
      />
    </>
  );
};
